import { Component } from "@kluntje/core";
import { LOADING, LOADED, INITIALIZED, IN_VIEWPORT, HIDDEN } from "Helper/cssClasses";
import { removeClass, addClass, onEvent, removeEvent } from "@kluntje/js-utils/lib/dom-helpers";
import { render } from "lit-html";
import { ViewportObserver } from "@kluntje/services";

import { renderLazyImage, FALLBACK_PLACEHOLDER } from "./fra-e-lazy-image.template"; // eslint-disable-line

class FRALazyImage extends Component {
  viewportObserver = ViewportObserver.getInstance();

  constructor() {
    super({
      ui: {
        image: ".fra-js-lazy-image__img :-one",
        wrapper: ".fra-js-lazy-image__wrapper :-one",
      },
      events: [
        {
          event: "kl-in-vp",
          target: "this",
          handler: "handleIntersectionChange",
        },
      ],
      initialStates: {
        imgLoaded: false,
      },
      useShadowDOM: true,
    });
  }

  /**
   * Returns value of 'src'-Attribute
   * @returns{string}
   */
  get imgSrc(): string {
    return this.getAttribute("src") || "";
  }

  /**
   * Returns value of 'srcset'-Attribute
   * @returns{string}
   */
  get imgSrcSet(): string {
    return this.getAttribute("srcset") || "";
  }

  /**
   * Returns value of 'sizes'-Attribute
   * @returns{string}
   */
  get imgSizes(): string {
    return this.getAttribute("sizes") || "";
  }

  /**
   * Returns value of 'alt'-Attribute
   * @returns{string}
   */
  get imgAlt(): string {
    return this.getAttribute("alt") || "";
  }

  /**
   * Returns value of 'aspect-ratio'-Attribute
   * @returns{string}
   */
  get imgAspectRatio(): string {
    return this.getAttribute("aspect-ratio") || "";
  }

  get imageAlignment(): string {
    return this.getAttribute("image-alignment") || "";
  }

  /**
   * Returns value of 'wrapper'-Attribute
   * @returns{string}
   */
  get imgWrapper(): string {
    return this.getAttribute("wrapper") || "";
  }

  /**
   * Returns, whether loaded image is Wider than needed
   * @returns{boolean}
   */
  get hasOverWidth(): boolean {
    return this.ui.image.offsetHeight < this.offsetHeight;
  }

  /**
   * Webcomponents Helper to observe Attribute Changes
   * @override
   * @returns{string[]}
   */
  static get observedAttributes(): string[] {
    return ["src", "aspect-ratio"];
  }

  /**
   * Handles change of observed Attributes
   * @override
   * @param {string} name - name of Attribute, that changed
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue || !this.state.initialized) return;

    switch (name) {
      case "src":
        if (this.state.imgLoaded) this.loadImage();
        break;
      case "aspect-ratio":
        this.updateComponent();
        this.handleOverwidth();
        break;
    }
  }

  /**
   * Renders Component via lit-html
   */
  renderComponent(): void {
    render(
      renderLazyImage({
        alt: this.imgAlt,
        src: FALLBACK_PLACEHOLDER,
        srcset: "",
        sizes: this.imgSizes,
        aspectRatio: this.imgAspectRatio,
        wrapper: this.imgWrapper,
        imageAlignment: this.imageAlignment,
      }),
      this.getUiRoot(),
    );
  }

  /**
   * Rerenders Component via lit-html
   */
  updateComponent(): void {
    render(
      renderLazyImage({
        alt: this.imgAlt,
        src: this.imgSrc,
        srcset: this.imgSrcSet,
        sizes: this.imgSizes,
        aspectRatio: this.imgAspectRatio,
        wrapper: this.imgWrapper,
        imageAlignment: this.imageAlignment,
      }),
      this.getUiRoot(),
    );
  }

  /**
   * Lifecycle-Hook: Initializes Component after render
   * @override
   */
  afterComponentRender(): void {
    addClass(this, INITIALIZED);
    this.viewportObserver.observe(this);
    removeClass(this, HIDDEN);
  }

  /**
   * Lifecycle-Hook for removing markup on destroy
   */
  beforeComponentDisconnects(): void {
    addClass(this, HIDDEN);
  }

  /**
   * Lifecycle-Hook for removing markup on destroy
   */
  destroyComponent(): void {
    this.setState({ imgLoaded: false });
  }

  /**
   * Handles Intersection Change, by removing viewportObserver
   * and initializing ImageLoad
   */
  handleIntersectionChange(): void {
    this.viewportObserver.unobserve(this);
    addClass(this, IN_VIEWPORT);
    removeEvent(this, "kl-in-vp", this.handleIntersectionChange, this);
    if (!this.state.imgLoaded) {
      this.loadImage();
    }
  }

  /**
   * Initiates Image Load, by add src(-set) to img in shadow-DOM
   */
  loadImage(): void {
    addClass(this.ui.wrapper, LOADING);
    const currentImage = this.ui.image;
    this.attachImageEvents(currentImage); // attach loading events
    // trigger image loading
    if (this.imgSrc || this.imgSrcSet) {
      if (this.imgSrc) currentImage.setAttribute("src", this.imgSrc);
      if (this.imgSrcSet) currentImage.setAttribute("srcset", this.imgSrcSet);
      if (this.getAttribute("ad-label")) this.style.setProperty("--ad-label", `"${this.getAttribute("ad-label")}"`);
    } else {
      console.warn("LazyImageLoader.loadImage: no src/srcset provided for ", currentImage);
    }
  }

  /**
   * Handles any ImageLoading-Errors
   */
  handleImageLoadingError(): void {
    const currentImg = this.ui.image;

    removeEvent(currentImg, "error", this.handleImageLoadingError, this);
    currentImg.setAttribute("src", "");
    currentImg.setAttribute("srcset", "");
  }

  /**
   * Handles ImageLoad-Event, by setting/removing needed Classes
   */
  handleImageLoad(): void {
    this.handleOverwidth();

    removeEvent(this.ui.image, "load", this.handleImageLoad, this);
    removeClass(this.ui.wrapper, LOADING);
    addClass(this, LOADED);
    this.setState({ imgLoaded: true });
  }

  /**
   * Adds needed Events for ImageLoading
   * @param {Node} img - image in Shadow-DOM
   */
  attachImageEvents(img: Node): void {
    onEvent(img, "load", this.handleImageLoad, this);
    onEvent(img, "error", this.handleImageLoadingError, this);
  }

  /**
   * Checks if image has Over-width and optionally adds needed class
   */
  handleOverwidth(): void {
    removeClass(this.ui.wrapper, "fra-e-lazy-image__wrapper--has-overwidth");
    if (this.hasOverWidth) {
      addClass(this.ui.wrapper, "fra-e-lazy-image__wrapper--has-overwidth");
    }
  }
}

customElements.define("fra-e-lazy-image", FRALazyImage);

export default FRALazyImage;
